import { KaraokeTrackManager } from '../../lib/KaraokeTrackManager';
import { AbstractCommand } from '../AbstractCommand';

import type { VideoEditor } from '../../VideoEditor';
import type { Command } from '../Command';

/**
 * Run a caption command on the SOURCE-timed caption set, whichever set the
 * caption track shows (PLAT-11905).
 *
 * While a dubbed language shows its dub-timed set, the source-timed set is
 * parked on the track. A command written for the source rows — re-texting them
 * from the translation — would otherwise read and write the dub-timed rows,
 * which must keep showing the voice's words. So this shows the source set for
 * the length of the command, runs it, and shows the dub-timed set again; the
 * same around its undo. No reconcile and no style change happens in between:
 * the move is the track's own swap of which set is shown. Once the dub-timed
 * set is back, its captions are derived again, because the command it wrapped
 * usually follows a translation edit, and each dub word's link to its
 * translation fragment must follow that edit at once.
 *
 * With the source set already shown, it simply runs the command.
 */
export class SourceSetScopeCommand extends AbstractCommand {
  private readonly inner: Command;

  constructor(editor: VideoEditor, inner: Command) {
    super(editor);
    this.inner = inner;
  }

  /**
   * The wrapped command's own name, so the command log and the save-trigger
   * list sent with analytics read exactly as they did before this wrapper.
   */
  getName(): string {
    return this.inner.getName();
  }

  getDescription(): string {
    return this.inner.getDescription();
  }

  /** Asked after `execute`, as the inner command expects. */
  shouldCreateUndoRedoEntry(): boolean {
    return this.inner.shouldCreateUndoRedoEntry();
  }

  execute(): boolean {
    return this.onSourceSet(() => this.inner.execute());
  }

  undo(): boolean {
    return this.onSourceSet(() => this.inner.undo());
  }

  private onSourceSet(run: () => boolean): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track || track.liveKind === 'source') return run();
    const dubClips = track.karaokeClips;
    track.showSourceSet();
    try {
      return run();
    } finally {
      track.showDubSet(dubClips);
      new KaraokeTrackManager(this.editor).reconcileDubCaptions();
    }
  }
}
